import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { fmtINR } from "@/lib/format";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Trash2, Pencil, Download, Upload, PowerOff, Power, Tag, Sparkles } from "lucide-react";

const TYPES = ["asset", "liability", "equity", "income", "expense"];

// Convert a signed opening_balance (Dr-positive convention stored in DB) to
// a natural balance the user actually wants to see:
//   asset / expense  → Dr side is natural. Positive stored → Dr.
//   liability / equity / income → Cr side is natural. Negative stored → Cr.
// Returns { natural: absolute-value-positive, side: "Dr"|"Cr", isReversed: bool }.
const naturalOb = (a) => {
  const signed = Number(a.opening_balance || 0);
  if (signed === 0) return { natural: 0, side: "", isReversed: false };
  const naturalIsDr = a.account_type === "asset" || a.account_type === "expense";
  const isDr = signed > 0;
  const onNaturalSide = (naturalIsDr && isDr) || (!naturalIsDr && !isDr);
  return {
    natural: Math.abs(signed),
    side: isDr ? "Dr" : "Cr",
    isReversed: !onNaturalSide,
  };
};

const naturalBal = (a) => {
  const signed = Number(a.balance || 0);
  if (signed === 0) return { natural: 0, side: "" };
  const isDr = signed > 0;
  return { natural: Math.abs(signed), side: isDr ? "Dr" : "Cr" };
};

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [ledger, setLedger] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [categoryTree, setCategoryTree] = useState({});
  const [form, setForm] = useState({
    code: "", name: "", account_type: "asset", gl_subcategory: "", subtype: "",
  });
  const [editForm, setEditForm] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOp, setBulkOp] = useState(null);   // "categorize" | "type" | "delete" | null
  const [bulkForm, setBulkForm] = useState({ account_type: "asset", gl_subcategory: "" });
  const [importPlan, setImportPlan] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [retroPlan, setRetroPlan] = useState(null);
  const [retroBusy, setRetroBusy] = useState(false);
  const importInputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/accounts", { params: q ? { q } : {} });
    setRows(r.data);
  };
  const loadCategories = async () => {
    try {
      const r = await api.get("/accounts/categories");
      setCategoryTree(r.data.tree || {});
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, [q]);
  useEffect(() => { loadCategories(); }, []);

  // Iter-127 · AI Re-categorize (uses existing POST /accounts/retro-categorize)
  const openRetro = async () => {
    setRetroBusy(true);
    try {
      const r = await api.post("/accounts/retro-categorize", { mode: "dry_run" });
      setRetroPlan(r.data);
      if (!r.data?.proposals?.length) {
        toast.info("Nothing to fix — every account is already well categorised.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    }
    setRetroBusy(false);
  };
  const commitRetro = async () => {
    if (!retroPlan) return;
    setRetroBusy(true);
    try {
      const r = await api.post("/accounts/retro-categorize", { mode: "commit" });
      toast.success(`Re-categorized ${r.data?.counts?.updated || 0} account(s)`);
      setRetroPlan(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    }
    setRetroBusy(false);
  };

  const openLedger = async (id) => {
    const r = await api.get(`/accounts/${id}/ledger`);
    setLedger(r.data);
  };

  const remove = async (e, a) => {
    e.stopPropagation();
    if (a.is_control) return toast.error("Control accounts (AR/AP) cannot be deleted");
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      const r = await api.delete(`/accounts/${a.id}`);
      if (r.data?.soft_deleted) toast.info(`Marked inactive — ${r.data.linked_vouchers} voucher line(s) reference it`);
      else toast.success("Deleted");
      load();
    } catch (err) {
      if (err?.response?.status === 409) {
        if (window.confirm(`${err.response.data.detail}\n\nSoft-delete (mark inactive) instead?`)) {
          await api.delete(`/accounts/${a.id}?force=true`);
          toast.info("Marked inactive"); load();
        }
      } else toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (e, a) => {
    e.stopPropagation();
    setEditForm({
      id: a.id, code: a.code || "", name: a.name || "",
      account_type: a.account_type, gl_subcategory: a.gl_subcategory || "",
      subtype: a.subtype || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const { id, ...patch } = editForm;
      await api.patch(`/accounts/${id}`, patch);
      toast.success("Account updated");
      setEditOpen(false); setEditForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const create = async () => {
    try {
      await api.post("/accounts", form);
      toast.success("Account added");
      setNewOpen(false);
      setForm({ code: "", name: "", account_type: "asset", gl_subcategory: "", subtype: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ---- Selection helpers ----
  const toggleSel = (e, id) => {
    e.stopPropagation();
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleSelAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const clearSel = () => setSelected(new Set());
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  // ---- Export XLSX ----
  const exportXlsx = async () => {
    try {
      const r = await api.get("/accounts/export.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "chart_of_accounts.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Chart of Accounts exported");
    } catch (e) { toast.error("Export failed"); }
  };

  // ---- Import XLSX (dry-run first) ----
  const pickImport = () => importInputRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file); setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/accounts/import.xlsx?mode=dry_run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPlan(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import parse failed");
    }
    setImportBusy(false);
    e.target.value = "";
  };
  const commitImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/accounts/import.xlsx?mode=commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created, updated, deleted, soft_deleted, protected: prot } = r.data;
      toast.success(
        `Import applied · ${created} created · ${updated} updated · ${deleted} deleted · ${soft_deleted} soft-deleted · ${prot} preserved`
      );
      setImportPlan(null); setImportFile(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImportBusy(false);
  };

  // ---- Bulk operations ----
  const runBulkCategorize = async () => {
    if (!bulkForm.gl_subcategory) { toast.error("Pick a sub-category"); return; }
    try {
      const r = await api.post("/accounts/bulk-categorize", {
        account_ids: Array.from(selected),
        gl_subcategory: bulkForm.gl_subcategory,
      });
      toast.success(`${r.data.updated} updated · ${r.data.skipped} skipped`);
      if (r.data.errors?.length) toast.warning(r.data.errors[0]);
      setBulkOp(null); clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runBulkType = async () => {
    if (!bulkForm.account_type) { toast.error("Pick a root type"); return; }
    if (!window.confirm(
      `Change root type to '${bulkForm.account_type}' for ${selected.size} account(s)?\n\n` +
      `Warning: this changes Schedule III bucketing. Control accounts will be skipped.`
    )) return;
    try {
      const r = await api.post("/accounts/bulk-update-type", {
        account_ids: Array.from(selected),
        account_type: bulkForm.account_type,
        gl_subcategory: bulkForm.gl_subcategory || undefined,
      });
      toast.success(`${r.data.updated} account(s) re-typed`);
      setBulkOp(null); clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runBulkToggle = async (isActive) => {
    if (!window.confirm(`${isActive ? "Activate" : "Deactivate"} ${selected.size} account(s)?`)) return;
    try {
      const r = await api.post("/accounts/bulk-toggle-active", {
        account_ids: Array.from(selected), is_active: isActive,
      });
      toast.success(`${r.data.updated} updated`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runBulkDelete = async () => {
    if (!window.confirm(
      `Delete ${selected.size} account(s)?\n\n` +
      `• Accounts with voucher lines will be soft-deleted (marked inactive)\n` +
      `• Control accounts (AR/AP) will be preserved`
    )) return;
    try {
      const r = await api.post("/accounts/bulk-delete", { account_ids: Array.from(selected) });
      const { deleted, soft_deleted, blocked } = r.data;
      toast.success(`${deleted} deleted · ${soft_deleted} soft-deleted · ${blocked.length} preserved`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Group by type + subcategory
  const grouped = useMemo(() => {
    return TYPES.map((t) => {
      const items = rows.filter((r) => r.account_type === t);
      const bySub = {};
      items.forEach((a) => {
        const key = a.gl_subcategory || "unclassified";
        (bySub[key] = bySub[key] || []).push(a);
      });
      return { type: t, items, bySub };
    });
  }, [rows]);

  const subLabel = (root, code) => {
    const list = categoryTree[root] || [];
    const hit = list.find((c) => c.code === code);
    return hit ? hit.label : code;
  };
  const subsForType = (t) => categoryTree[t] || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="accounts-page">
      <PageHeader
        eyebrow="Accounting"
        title="Chart of Accounts"
        description="GL backbone — organised by root category and Schedule III sub-category. Export to Excel, edit in bulk, and re-upload changes in one round-trip."
        actions={
          <>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Filter accounts…" value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-sm h-9 pl-8 w-56" data-testid="accounts-search" />
            </div>
            <Button variant="outline" onClick={openRetro} disabled={retroBusy} className="rounded-sm gap-1.5" data-testid="accounts-retro-btn">
              <Sparkles className="w-4 h-4" />{retroBusy ? "Scanning…" : "AI Re-categorize"}
            </Button>
            <Button variant="outline" onClick={exportXlsx} className="rounded-sm" data-testid="accounts-export-btn">
              <Download className="w-4 h-4 mr-1.5" />Export XLSX
            </Button>
            <Button variant="outline" onClick={pickImport} className="rounded-sm" data-testid="accounts-import-btn">
              <Upload className="w-4 h-4 mr-1.5" />Import XLSX
            </Button>
            <input ref={importInputRef} type="file" accept=".xlsx"
                   onChange={onImportFile} className="hidden"
                   data-testid="accounts-import-file" />
            <Button onClick={() => setNewOpen(true)}
              className="rounded-sm bg-accent hover:bg-accent/90 text-white"
              data-testid="new-account-btn">+ Account</Button>
          </>
        }
      />

      {/* ---- Bulk edit toolbar ---- */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 border rounded-sm bg-amber-50 dark:bg-amber-950/40 px-4 py-3 flex items-center gap-2 flex-wrap"
             data-testid="bulk-toolbar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setBulkOp("categorize")}
                  data-testid="bulk-categorize-btn">
            <Tag className="w-3.5 h-3.5 mr-1.5" />Change Sub-category
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkOp("type")}
                  data-testid="bulk-type-btn">
            <Tag className="w-3.5 h-3.5 mr-1.5" />Change Root Type
          </Button>
          <Button size="sm" variant="outline" onClick={() => runBulkToggle(true)}
                  data-testid="bulk-activate-btn">
            <Power className="w-3.5 h-3.5 mr-1.5" />Activate
          </Button>
          <Button size="sm" variant="outline" onClick={() => runBulkToggle(false)}
                  data-testid="bulk-deactivate-btn">
            <PowerOff className="w-3.5 h-3.5 mr-1.5" />Deactivate
          </Button>
          <Button size="sm" variant="destructive" onClick={runBulkDelete}
                  data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSel}>Clear</Button>
        </div>
      )}

      {/* Iter-199 · Wipe ALL non-control accounts — typed confirmation required */}
      <div className="flex justify-end">
        <Button size="sm" variant="outline"
                  className="text-red-600 border-red-500/60 hover:bg-red-50"
                  data-testid="accounts-wipe-all"
                  onClick={async () => {
                    const phrase = window.prompt("⚠︎ Erase ALL non-control accounts?\n\nControl accounts (AR/AP) survive.\nVoucher-referenced ones → soft-deleted.\nOrphans → hard-deleted.\n\nType   WIPE ALL DATA   to confirm:");
                    if (phrase !== "WIPE ALL DATA") { if (phrase !== null) toast.error("Confirmation phrase did not match — nothing wiped"); return; }
                    try {
                      const r = await api.post("/accounts/wipe-all", { confirm: "WIPE ALL DATA" });
                      toast.success(`Wiped ${r.data.deleted} · soft-deleted ${r.data.soft_deleted}`);
                      clearSel(); load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Wipe failed"); }
                  }}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete ALL Accounts
        </Button>
      </div>

      <div className="space-y-6">
        {grouped.map((g) =>
          g.items.length > 0 && (
            <div key={g.type} className="card-sharp overflow-hidden" data-testid={`gl-group-${g.type}`}>
              <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{g.type}</div>
                <div className="text-xs text-muted-foreground">{g.items.length} accounts</div>
              </div>
              {Object.entries(g.bySub).map(([subKey, subAccs]) => (
                <div key={subKey} className="border-b last:border-b-0" data-testid={`gl-sub-${g.type}-${subKey}`}>
                  <div className="px-4 py-2 bg-background/50 text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                    <span>{subKey === "unclassified" ? "Unclassified" : subLabel(g.type, subKey)}</span>
                    <span>{subAccs.length}</span>
                  </div>
                  <table className="w-full dense">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>
                          <Checkbox
                            checked={subAccs.every((a) => selected.has(a.id)) && subAccs.length > 0}
                            onCheckedChange={() => {
                              const next = new Set(selected);
                              const allSel = subAccs.every((a) => next.has(a.id));
                              subAccs.forEach((a) => allSel ? next.delete(a.id) : next.add(a.id));
                              setSelected(next);
                            }}
                            data-testid={`bulk-sel-sub-${subKey}`}
                          />
                        </th>
                        <th style={{ width: 100 }}>Code</th>
                        <th>Name</th>
                        <th>Subtype</th>
                        <th style={{ width: 100 }}>Control</th>
                        <th className="text-right" style={{ width: 180 }}>Balance</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subAccs.map((a) => {
                        const nb = naturalBal(a);
                        return (
                        <tr key={a.id}
                            className={`cursor-pointer ${a.is_active === false ? "opacity-50" : ""} ${selected.has(a.id) ? "bg-amber-50/50 dark:bg-amber-950/20" : ""}`}
                            onClick={() => openLedger(a.id)}
                            data-testid={`account-row-${a.code}`}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={selected.has(a.id)}
                                      onCheckedChange={(v) => toggleSel({ stopPropagation: () => {} }, a.id)}
                                      data-testid={`sel-${a.code}`} />
                          </td>
                          <td className="font-mono text-xs">{a.code}</td>
                          <td>
                            {a.name}
                            {a.is_active === false && (
                              <span className="text-[10px] text-muted-foreground ml-1">(inactive)</span>
                            )}
                          </td>
                          <td className="text-xs text-muted-foreground">{a.subtype || "—"}</td>
                          <td>
                            {a.is_control
                              ? <span className="text-[10px] font-mono uppercase text-accent">Control</span>
                              : "—"}
                          </td>
                          <td className="text-right tabular-nums font-mono">
                            {nb.natural === 0 ? "—" : (
                              <>
                                {fmtINR(nb.natural)}
                                <span className="text-[10px] font-mono text-muted-foreground ml-1">{nb.side}</span>
                              </>
                            )}
                          </td>
                          <td className="text-right">
                            <button className="text-muted-foreground hover:text-accent p-1"
                              onClick={(ev) => startEdit(ev, a)}
                              data-testid={`edit-account-${a.code}`} title="Edit categorization">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {!a.is_control && (
                              <button className="text-muted-foreground hover:text-destructive p-1 ml-1"
                                onClick={(ev) => remove(ev, a)}
                                data-testid={`delete-account-${a.code}`} title="Delete account">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* ---- Retro-categorize preview dialog ---- */}
      <Dialog open={!!retroPlan} onOpenChange={(o) => !o && setRetroPlan(null)}>
        <DialogContent className="max-w-2xl" data-testid="retro-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />AI Re-categorize Chart of Accounts
            </DialogTitle>
          </DialogHeader>
          {retroPlan && (
            <div className="space-y-4 text-sm">
              <div className="text-xs text-muted-foreground">
                Scanned <b>{retroPlan.counts.scanned}</b> accounts in the "Other Current / Other Expense / Other Income" fallback bucket.
                The name heuristic can confidently re-categorize <b>{retroPlan.counts.proposals}</b> of them.
              </div>
              {retroPlan.proposals.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 border rounded-sm">
                  Nothing to fix. Your CoA is already well-categorized.
                </div>
              ) : (
                <div className="border rounded-sm max-h-[380px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left p-2">Account</th>
                        <th className="text-left p-2">From</th>
                        <th className="text-left p-2">→ To</th>
                        <th className="text-left p-2">Conf.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retroPlan.proposals.map((p) => (
                        <tr key={p.account_id} className="border-t">
                          <td className="p-2"><span className="font-mono text-[10px]">{p.code}</span> {p.name}</td>
                          <td className="p-2 text-muted-foreground">
                            <span className="font-mono text-[10px]">{p.from_type}</span> · {p.from_sub}
                          </td>
                          <td className="p-2">
                            <span className="font-mono text-[10px] text-accent">{p.to_type}</span> · <b>{p.to_sub}</b>
                          </td>
                          <td className="p-2">
                            <span className={p.confidence === "high" ? "text-emerald-600" : "text-amber-600"}>
                              {p.confidence}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetroPlan(null)}>Cancel</Button>
            {retroPlan && retroPlan.proposals.length > 0 && (
              <Button onClick={commitRetro} disabled={retroBusy}
                      className="bg-accent hover:bg-accent/90 text-white"
                      data-testid="retro-commit">
                {retroBusy ? "Applying…" : `Apply ${retroPlan.proposals.length} change(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Bulk sub-category dialog ---- */}
      <Dialog open={bulkOp === "categorize"} onOpenChange={(o) => !o && setBulkOp(null)}>
        <DialogContent data-testid="bulk-categorize-dialog">
          <DialogHeader><DialogTitle>Change GL Sub-category — {selected.size} accounts</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">
            All selected accounts will be re-tagged to this sub-category.
            Rows whose root type doesn't allow this sub-category will be skipped.
          </div>
          <Label className="mt-3">New Sub-category</Label>
          <Select value={bulkForm.gl_subcategory}
                  onValueChange={(v) => setBulkForm({ ...bulkForm, gl_subcategory: v })}>
            <SelectTrigger data-testid="bulk-sub-select"><SelectValue placeholder="Pick sub-category" /></SelectTrigger>
            <SelectContent>
              {TYPES.flatMap((t) =>
                (categoryTree[t] || []).map((s) => (
                  <SelectItem key={`${t}:${s.code}`} value={s.code}>{t} · {s.label}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOp(null)}>Cancel</Button>
            <Button onClick={runBulkCategorize} data-testid="bulk-categorize-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Bulk root-type dialog ---- */}
      <Dialog open={bulkOp === "type"} onOpenChange={(o) => !o && setBulkOp(null)}>
        <DialogContent data-testid="bulk-type-dialog">
          <DialogHeader><DialogTitle>Change Root Type — {selected.size} accounts</DialogTitle></DialogHeader>
          <div className="text-xs text-destructive">
            ⚠ Changing root type re-buckets these accounts in the Balance Sheet / P&L.
            Control accounts (AR/AP) in the selection will be skipped.
          </div>
          <Label className="mt-3">New Root Type</Label>
          <Select value={bulkForm.account_type}
                  onValueChange={(v) => setBulkForm({ account_type: v, gl_subcategory: "" })}>
            <SelectTrigger data-testid="bulk-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="mt-3">Sub-category (optional)</Label>
          <Select value={bulkForm.gl_subcategory}
                  onValueChange={(v) => setBulkForm({ ...bulkForm, gl_subcategory: v })}>
            <SelectTrigger><SelectValue placeholder="Leave empty to keep existing" /></SelectTrigger>
            <SelectContent>
              {(categoryTree[bulkForm.account_type] || []).map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOp(null)}>Cancel</Button>
            <Button onClick={runBulkType} data-testid="bulk-type-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Import dry-run preview dialog ---- */}
      <Dialog open={!!importPlan} onOpenChange={(o) => !o && (setImportPlan(null), setImportFile(null))}>
        <DialogContent className="max-w-2xl" data-testid="import-preview-dialog">
          <DialogHeader><DialogTitle>Import Preview — dry run</DialogTitle></DialogHeader>
          {importPlan && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-5 gap-2 text-center">
                <div className="border rounded-sm p-2">
                  <div className="text-2xl font-mono text-emerald-600">{importPlan.counts.creates}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Create</div>
                </div>
                <div className="border rounded-sm p-2">
                  <div className="text-2xl font-mono text-amber-600">{importPlan.counts.updates}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Update</div>
                </div>
                <div className="border rounded-sm p-2">
                  <div className="text-2xl font-mono text-destructive">{importPlan.counts.deletes}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Delete</div>
                </div>
                <div className="border rounded-sm p-2">
                  <div className="text-2xl font-mono text-slate-500">{importPlan.counts.protected}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Preserved</div>
                </div>
                <div className="border rounded-sm p-2">
                  <div className="text-2xl font-mono text-destructive">{importPlan.counts.errors}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Errors</div>
                </div>
              </div>

              {importPlan.errors.length > 0 && (
                <div className="border border-destructive/50 rounded-sm p-3 bg-destructive/5 max-h-40 overflow-y-auto">
                  <div className="text-xs font-medium text-destructive mb-1">Validation Errors — fix these first</div>
                  {importPlan.errors.map((e, i) => (
                    <div key={i} className="text-xs text-destructive">{e}</div>
                  ))}
                </div>
              )}

              {importPlan.deletes.length > 0 && (
                <details className="border rounded-sm">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-destructive">
                    ▶ {importPlan.deletes.length} account(s) will be deleted
                  </summary>
                  <div className="px-3 py-2 max-h-40 overflow-y-auto space-y-1">
                    {importPlan.deletes.map((d, i) => (
                      <div key={i} className="text-xs font-mono flex justify-between">
                        <span>{d.code} · {d.name}</span>
                        <span className="text-muted-foreground">balance {fmtINR(d.balance)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {importPlan.updates.length > 0 && (
                <details className="border rounded-sm">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-amber-700">
                    ▶ {importPlan.updates.length} account(s) will be updated
                  </summary>
                  <div className="px-3 py-2 max-h-40 overflow-y-auto space-y-1">
                    {importPlan.updates.map((u) => (
                      <div key={u.id} className="text-xs">
                        <span className="font-mono">{u.code} · {u.name}</span>
                        {Object.entries(u.changes).map(([k, c]) => (
                          <span key={k} className="ml-2 text-muted-foreground">
                            {k}: <span className="line-through">{String(c.before)}</span> → <b>{String(c.after)}</b>
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {importPlan.creates.length > 0 && (
                <details className="border rounded-sm">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-emerald-700">
                    ▶ {importPlan.creates.length} account(s) will be created
                  </summary>
                  <div className="px-3 py-2 max-h-40 overflow-y-auto space-y-1">
                    {importPlan.creates.map((c, i) => (
                      <div key={i} className="text-xs font-mono">
                        {c.code} · {c.name} <span className="text-muted-foreground">({c.account_type} · {c.gl_subcategory})</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportPlan(null); setImportFile(null); }}
                    data-testid="import-cancel">Cancel</Button>
            <Button onClick={commitImport}
                    disabled={importBusy || (importPlan?.counts.errors > 0)}
                    data-testid="import-commit">
              {importBusy ? "Applying…" : "Confirm & Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Ledger drawer ---- */}
      <Sheet open={!!ledger} onOpenChange={(o) => !o && setLedger(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto" data-testid="ledger-drawer">
          {ledger && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading">
                  <span className="font-mono text-xs text-muted-foreground">{ledger.account.code}</span> · {ledger.account.name}
                </SheetTitle>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Ledger · closing {fmtINR(ledger.closing_balance)}
                </div>
              </SheetHeader>
              <table className="w-full dense mt-4">
                <thead><tr>
                  <th>Date</th><th>Voucher</th><th>Narration</th>
                  <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th>
                </tr></thead>
                <tbody>
                  {ledger.entries.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No entries yet</td></tr>
                  )}
                  {ledger.entries.map((e, i) => (
                    <tr key={i}>
                      <td className="text-xs">{e.date}</td>
                      <td className="font-mono text-xs">{e.voucher_number}</td>
                      <td className="text-xs">{e.narration || "—"}</td>
                      <td className="text-right tabular-nums">{e.debit ? fmtINR(e.debit) : "—"}</td>
                      <td className="text-right tabular-nums">{e.credit ? fmtINR(e.credit) : "—"}</td>
                      <td className="text-right tabular-nums font-mono">{fmtINR(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ---- Create ---- */}
      <Sheet open={newOpen} onOpenChange={setNewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-4">
          <SheetHeader><SheetTitle className="font-heading">New Account</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                     className="rounded-sm mt-1" data-testid="new-acc-code" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="rounded-sm mt-1" data-testid="new-acc-name" />
            </div>
            <div>
              <Label>Root category</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v, gl_subcategory: "" })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-acc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>GL Sub-category</Label>
              <Select value={form.gl_subcategory} onValueChange={(v) => setForm({ ...form, gl_subcategory: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-acc-subcategory">
                  <SelectValue placeholder="Auto-derive from name" />
                </SelectTrigger>
                <SelectContent>
                  {subsForType(form.account_type).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">Leave empty to auto-derive.</div>
            </div>
            <div>
              <Label>Subtype (optional hint)</Label>
              <Input value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })}
                     className="rounded-sm mt-1" placeholder="e.g. bank, operating" />
            </div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white"
                  data-testid="new-acc-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* ---- Edit ---- */}
      <Sheet open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditForm(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-4">
          <SheetHeader><SheetTitle className="font-heading">Edit Account</SheetTitle></SheetHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label>Code</Label>
                <Input value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                       className="rounded-sm mt-1" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                       className="rounded-sm mt-1" />
              </div>
              <div>
                <Label>Root category</Label>
                <Select value={editForm.account_type} onValueChange={(v) => setEditForm({ ...editForm, account_type: v, gl_subcategory: "" })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="edit-acc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>GL Sub-category</Label>
                <Select value={editForm.gl_subcategory} onValueChange={(v) => setEditForm({ ...editForm, gl_subcategory: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="edit-acc-subcategory">
                    <SelectValue placeholder="Pick sub-category" />
                  </SelectTrigger>
                  <SelectContent>
                    {subsForType(editForm.account_type).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subtype (legacy hint)</Label>
                <Input value={editForm.subtype} onChange={(e) => setEditForm({ ...editForm, subtype: e.target.value })}
                       className="rounded-sm mt-1" />
              </div>
              <Button onClick={saveEdit} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white"
                      data-testid="edit-acc-save">Save Changes</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
